import { api } from '@/apis';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TripsAccelerationSummariesCountParams } from './types';

export function useTripAccelerationSummariesCount({
  startTime,
  endTime,
  query: filter,
  mode,
  excludeExempt,
}: TripsAccelerationSummariesCountParams) {
  const queryKey = [
    'tripAccelerationSummariesCount',
    startTime,
    endTime,
    filter,
    mode,
    excludeExempt,
  ];
  const queryClient = useQueryClient();
  const query = useQuery<number>({
    queryKey,
    queryFn: async ({ signal }) => {
      const peopleResponse: { code: string }[] = await api
        .post('pipeline/people', {
          json: [{ $match: filter }, { $project: { _id: false, code: true } }],
        })
        .json();

      const exemptTripClassifications: string[] = excludeExempt
        ? (
            await api
              .post<{ value: string }[]>('pipeline/options', {
                json: [
                  {
                    $match: {
                      name: 'tripClassification',
                      excludeFromDriverScores: true,
                    },
                  },
                  { $project: { _id: false, value: true } },
                ],
                signal,
              })
              .json()
          ).map((option) => option.value)
        : [];

      const response: { total: number }[] = await api
        .post('pipeline/vehicleTripAccelerationSummaries', {
          json: [
            {
              $match: {
                startTime: { $gte: startTime, $lt: endTime },
                durationSeconds: { $gte: 60 },
                'driver.code': {
                  $in: peopleResponse.map((person) => person.code),
                },
                ...(excludeExempt
                  ? { classification: { $nin: exemptTripClassifications } }
                  : {}),
                ...(mode === 'emergency'
                  ? { 'equipmentActivations.emergencyOn': true }
                  : mode === 'nonEmergency'
                    ? { 'equipmentActivations.emergencyOn': { $ne: true } }
                    : {}),
              },
            },
            { $count: 'total' },
          ],
          signal,
        })
        .json();

      return response[0]?.total ?? 0;
    },
    placeholderData: (previousData) => previousData ?? 0,
    staleTime: 1000 * 60 * 60,
  });

  return {
    query,
    cancel: () => queryClient.cancelQueries({ queryKey }),
  };
}
